import { Connection } from '@lucidtech/las-sdk-browser';
import React, { Key, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComboBox, ComboBoxProps, ComboBoxStateContext, FieldError } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Flex, Input, InputProps, Label, ListBox, ListBoxItem, Popover, Text } from '@/components/core';
import { ChevronIcon, CloseIcon, ExcelIcon, OneDriveFolderIcon, SaveIcon, SearchIcon } from '@/components/icons';
import { useDebounceCallback } from '@/hooks';
import { useTriggerStore } from '@/store';
import { isArray } from '@/utils';

import { GraphObject } from '../client';
import { useGraphClient } from '../hooks';

function getIconForItemType(itemType?: 'workbook' | 'folder', dim?: boolean) {
  const styles = ['size-4', dim ? 'opacity-55' : ''];

  if (itemType === 'folder') {
    return <OneDriveFolderIcon className={merge(styles)} />;
  } else if (itemType === 'workbook') {
    return <ExcelIcon className={merge(styles)} />;
  } else {
    return null;
  }
}

type OneDriveItemInputProps = {
  currentItem?: GraphObject;
  isLoading?: boolean;
  itemType?: 'workbook' | 'folder';
} & InputProps;

const OneDriveItemInput = ({ currentItem, itemType, isLoading, ...rest }: OneDriveItemInputProps) => {
  const state = React.useContext(ComboBoxStateContext);

  const onClear = useCallback(() => {
    state?.setSelectedKey(null);
    state?.close();
  }, [state]);

  const onFocus = useCallback(() => {
    state?.open();
  }, [state]);

  return currentItem ? (
    <Flex className="gap-xs w-full items-center">
      <Flex
        variant="plain"
        className="p-xs gap-sm px-sm w-full items-center rounded-md border-1 border-gray-200 bg-gray-50 hover:bg-gray-100"
      >
        {getIconForItemType(itemType, true)}
        <Text variant="plain" slot="description" className="grow">
          {currentItem?.name}
        </Text>

        <CloseIcon onClick={onClear} className="cursor-pointer" />
      </Flex>
    </Flex>
  ) : (
    <>
      <Input {...rest} onFocus={onFocus} className="relative ps-5">
        {isLoading ? <SaveIcon className="x-0 absolute size-4" /> : <SearchIcon className="x-0 absolute size-4" />}

        <Button className="p-0">
          <ChevronIcon rotation="down" />
        </Button>
      </Input>
    </>
  );
};

interface FileSelectorProps<T extends object> extends Omit<ComboBoxProps<T>, 'children'> {
  connection?: Connection;
  label?: string;
  description?: string;
  itemType?: 'workbook' | 'folder';
  requestElevatedPermission?: () => void;
}

const IFileSelector = <T extends object>({
  connection,
  label,
  description,
  itemType,
  requestElevatedPermission,
}: FileSelectorProps<T>) => {
  const graphClient = useGraphClient(connection);
  const [isSearching, setIsSearching] = useState(false);
  const updateAction = useTriggerStore((state) => state.updateAction);

  const currentDriveItemId = useTriggerStore((state) => state.action.config?.driveItemId) as string | undefined;
  const currentDriveId = useTriggerStore((state) => state.action.config?.driveId) as string | undefined;
  const [currentItem, setCurrentItem] = useState<GraphObject | undefined>(undefined);

  const [items, setItems] = useState<GraphObject[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const errorMessage = undefined;

  const searchFilter = useMemo(
    () => (itemType === 'folder' ? 'IsContainer:true AND IsDocument:false' : 'filetype:xlsx'),
    [itemType]
  );

  const onSearchCompleted = useCallback((items: GraphObject[] | undefined) => {
    setItems(items || []);
    setIsSearching(false);
  }, []);

  const onSearch = useDebounceCallback(
    useCallback(
      async (query?: string) => {
        setIsSearching(true);

        if (query?.startsWith('https://')) {
          const searchableKeys = ['sourcedoc', 'id', 'itemId'];
          const url = new URL(query);

          for (const key of searchableKeys) {
            if (url.searchParams.has(key)) {
              const item = url.searchParams.get(key);
              if (item) {
                graphClient.search(item).then(onSearchCompleted);
              }
              break;
            }
          }
        } else {
          graphClient?.search(`${query ?? ''} ${searchFilter}`).then(onSearchCompleted);
        }
      },
      [graphClient, onSearchCompleted, searchFilter]
    ),
    500
  );

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      if (key === undefined) return;

      const obj = items.find((obj) => obj.id === key);
      setCurrentItem(obj);

      if (key === null) {
        updateAction((state) => ({
          config: {
            ...state.config,
            siteId: null,
            driveId: null,
            driveItemId: null,
          },
        }));
      } else {
        const driveItemId = key?.toString() || null;

        if (obj) {
          updateAction((state) => ({
            config: {
              ...state.config,
              siteId: obj.siteId!,
              driveId: obj.driveId!,
              parentId: itemType === 'folder' ? driveItemId : obj.parentId!,
              driveItemId,
              tableId: null,
              fieldToColumnMap: {},
            },
          }));
        }
      }
    },
    [itemType, items, updateAction]
  );

  useEffect(() => {
    setIsSearching(true);
    graphClient
      ?.search(searchFilter)
      .then((items) => {
        if (!items) return;
        setItems(items);

        if (currentDriveId && currentDriveItemId) {
          const item = items.find((it) => it.id === currentDriveItemId);
          if (item) {
            setCurrentItem(item);
          } else {
            graphClient?.get(currentDriveId, currentDriveItemId).then((go) => {
              setItems((prev) => [...prev, go]);
              setCurrentItem(go);
            });
          }
        } else {
          setCurrentItem(undefined);
        }
      })
      .finally(() => setIsSearching(false));
  }, [currentDriveId, currentDriveItemId, graphClient, searchFilter]);

  const placeholder = useMemo(
    () => ({
      id: 'placeholder',
    }),
    []
  );

  const requestedScopes = connection?.config?.scopes as string[] | undefined;
  const canElevatePermissions = isArray(requestedScopes) && !requestedScopes.includes('Files.ReadWrite.All');

  return (
    <>
      <ComboBox
        items={items.length > 0 ? items : [placeholder]}
        onSelectionChange={onSelectionChange}
        menuTrigger="input"
        defaultSelectedKey={currentDriveItemId}
        onInputChange={onSearch}
        key={itemType}
      >
        <Flex col variant="plain" className="gap-xs">
          <Label isRequired>{label}</Label>
          <Flex>
            <OneDriveItemInput
              placeholder={itemType === 'workbook' ? `Search or paste OneDrive URL` : 'Search your OneDrive'}
              isLoading={isSearching}
              currentItem={currentItem}
              ref={inputRef}
              itemType={itemType}
            />
          </Flex>
        </Flex>
        {!currentItem && <Text slot="description">{description}</Text>}
        <FieldError>{errorMessage}</FieldError>

        <Popover>
          <ListBox
            className="max-h-[20rem] overflow-auto"
            style={{ width: inputRef.current?.getBoundingClientRect().width }}
          >
            {(obj: GraphObject) => (
              <ListBoxItem id={obj.id} key={obj.id} textValue={obj.name}>
                {obj.id === 'placeholder' ? (
                  <Flex col variant="warning" className="gap-sm p-sm items-start rounded-sm">
                    {canElevatePermissions ? (
                      <>
                        <Text>
                          <strong>No files found.</strong> If your files are stored in a shared Site or SharePoint
                          directory, you can try requesting additional access permissions. This may require admin
                          approval.
                        </Text>
                        <Button variant="secondary" onPress={requestElevatedPermission}>
                          <Text size="xs">Request additional access</Text>
                        </Button>
                      </>
                    ) : (
                      <Text>
                        <strong>No files found.</strong> Please make sure the files are shared with you, or contact your
                        administrator for assistance.
                      </Text>
                    )}
                  </Flex>
                ) : (
                  <Flex className="gap-sm items-center">
                    {getIconForItemType(itemType)}
                    <Text>{obj.name}</Text>
                  </Flex>
                )}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>
    </>
  );
};

export const FileSelector = memo(IFileSelector) as typeof IFileSelector;
